import { DocKind, EvalStatus, RuleDef, RuleMode, RulePeriod, RuleScopeType, RuleSnapshot } from './rulesTypes';

export const BORDER = '#e2e8f0';
export const DARK = '#1e293b';
export const SOFT = '#f8fafc';
export const MUTED = '#64748b';
export const DANGER = '#ef4444';
export const PRIMARY = '#1976d2';
export const PRIMARY_DARK = '#1565c0';
export const PRIMARY_SOFT = '#eff5fc';
export const TRACK = '#f1f5f9';
export const PANEL_SHADOW = 'none';
export const FLOAT_SHADOW = '0 6px 20px rgba(15,23,42,0.14)';

/** Общий вид полей окна: светлые, скруглённые, цвет фокуса - из темы сайта */
export const FIELD_SX = {
  '& .MuiOutlinedInput-root': { borderRadius: '6px', bgcolor: '#fff', fontSize: 13.5 },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: BORDER },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#cbd5e1' },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: PRIMARY, borderWidth: 1.5 },
  '& .MuiInputLabel-root': { fontSize: 13.5 },
  '& .MuiInputLabel-root.Mui-focused': { color: PRIMARY }
} as const;

/** Выпадающий список аптек: карточка с рамкой, группы-заголовки по регионам */
export const AUTOCOMPLETE_SLOT_PROPS = {
  paper: {
    sx: {
      mt: 0.5, borderRadius: '8px', border: `1px solid ${BORDER}`, boxShadow: FLOAT_SHADOW,
      '& .MuiAutocomplete-listbox': { p: 0.75 },
      '& .MuiAutocomplete-option': { fontSize: 13.5, color: DARK, borderRadius: 1.5, minHeight: 36 },
      '& .MuiAutocomplete-option.Mui-focused': { bgcolor: TRACK },
      '& .MuiAutocomplete-option[aria-selected="true"]': { bgcolor: PRIMARY_SOFT, color: PRIMARY_DARK, fontWeight: 600 },
      '& .MuiAutocomplete-groupLabel': { fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: MUTED, lineHeight: '30px' },
      '& .MuiAutocomplete-noOptions': { fontSize: 13, color: MUTED }
    }
  }
} as const;

/** Выпадающее меню обычного селекта - в том же стиле */
export const SELECT_MENU_PROPS = {
  slotProps: {
    paper: {
      sx: {
        mt: 0.5, borderRadius: '8px', border: `1px solid ${BORDER}`, boxShadow: FLOAT_SHADOW,
        '& .MuiMenuItem-root': { fontSize: 13.5, color: DARK, borderRadius: 1.5, mx: 0.75, my: 0.25 },
        '& .MuiMenuItem-root.Mui-selected': { bgcolor: PRIMARY_SOFT, color: PRIMARY_DARK, fontWeight: 600 }
      }
    }
  }
} as const;

export const BTN_SX = { textTransform: 'none', fontWeight: 600, borderRadius: '8px' } as const;

/** Основная кнопка окна: штатный синий сайта */
export const PRIMARY_BTN_SX = {
  ...BTN_SX, bgcolor: PRIMARY, boxShadow: 'none', '&:hover': { bgcolor: PRIMARY_DARK, boxShadow: 'none' },
  '&.Mui-disabled': { bgcolor: '#e2e8f0', color: '#94a3b8' }
} as const;

/** Вторичная кнопка: светлая с рамкой */
export const GHOST_BTN_SX = {
  ...BTN_SX, color: MUTED, border: `1px solid ${BORDER}`, bgcolor: '#fff', '&:hover': { bgcolor: SOFT, borderColor: '#cbd5e1' }
} as const;

export const KIND_ORDER: DocKind[] = ['direct', 'via_rc', 'return'];

export const KIND_LABELS: Record<DocKind, string> = {
  direct: 'Прямое перемещение',
  via_rc: 'Перемещение через РЦ',
  return: 'Возврат'
};

export const MODE_LABELS: Record<RuleMode, string> = { off: 'Выключено', observe: 'Наблюдение', enforce: 'Блокирует' };

/** Короткие пояснения режимов (подсказки в селекте и на вкладке «Правила») */
export const MODE_HINTS: Record<RuleMode, string> = {
  off: 'Правило не проверяется',
  observe: 'Нарушения пишутся в журнал, документ создаётся',
  enforce: 'Документ с нарушением не создаётся'
};

export const MODE_COLORS: Record<RuleMode, { color: string; bg: string }> = {
  off: { color: '#64748b', bg: '#f1f5f9' },
  observe: { color: '#b45309', bg: '#fffbeb' },
  enforce: { color: '#b91c1c', bg: '#fef2f2' }
};

export const PERIOD_LABELS: Record<RulePeriod, string> = { day: 'в день', week: 'в неделю', month: 'в месяц' };

export const SCOPE_LABELS: Record<RuleScopeType, string> = { network: 'Вся сеть', direction: 'Направление', pharmacy: 'Аптека' };

export const ACTION_LABELS: Record<'create' | 'update' | 'delete', string> = { create: 'Создано', update: 'Изменено', delete: 'Удалено' };

export const ACTION_COLORS: Record<'create' | 'update' | 'delete', { color: string; bg: string }> = {
  create: { color: '#15803d', bg: '#f0fdf4' },
  update: { color: '#1d4ed8', bg: '#eff6ff' },
  delete: { color: '#b91c1c', bg: '#fef2f2' }
};

export const EVAL_LABELS: Record<EvalStatus, string> = {
  ok: 'Выполняется',
  violated: 'Нарушено',
  skipped: 'Не проверено',
  off: 'Выключено',
  not_set: 'Не задано',
  error: 'Ошибка проверки'
};

export const EVAL_COLORS: Record<EvalStatus, { color: string; bg: string }> = {
  ok: { color: '#15803d', bg: '#f0fdf4' },
  violated: { color: '#b91c1c', bg: '#fef2f2' },
  skipped: { color: '#b45309', bg: '#fffbeb' },
  off: { color: '#64748b', bg: '#f1f5f9' },
  not_set: { color: '#64748b', bg: '#f1f5f9' },
  error: { color: '#b91c1c', bg: '#fef2f2' }
};

export const SKIP_REASONS: Record<string, string> = {
  no_direction: 'не выбран получатель',
  no_items: 'не указаны позиции документа',
  prices_unavailable: 'цены недоступны (сервис номенклатуры)'
};

export const PERM_HINTS_RULES = {
  networkReadOnly: 'Правила сети может менять только оператор с зоной «Все». У вас доступ на уровне аптек - доступны исключения для ваших аптек.',
  noSenders: 'В вашей зоне нет аптек, для которых можно задать исключение'
};

/** Нижняя граница значения: у периодических правил 0 = «направление закрыто», у целых - 1, у денежных - 0,01 (как на сервере, store.parseInput) */
export const minValue = (def: RuleDef): number => (def.periodic ? 0 : def.integer ? 1 : 0.01);

/** Ошибка введённого значения правила (null - всё хорошо). У выключенного правила значение не проверяется */
export function valueError(def: RuleDef, mode: RuleMode, raw: string): string | null {
  if (mode === 'off') return null;
  const n = Number(String(raw).replace(',', '.'));
  if (raw === '' || !Number.isFinite(n)) return 'Введите число';
  if (n < minValue(def)) return `Не меньше ${minValue(def)}`;
  if (def.integer && !Number.isInteger(n)) return 'Только целое число';
  return null;
}

/** Формат значения правила: «3 док. в день», «5 000 ₸», «3 поз.» */
export function fmtRuleValue(def: RuleDef | undefined, value: number, period?: RulePeriod | null): string {
  const num = Number(value).toLocaleString('ru-RU', { maximumFractionDigits: 2 });
  if (!def) return num;
  const base = `${num} ${def.unit}`;
  return def.periodic && period ? `${base} ${PERIOD_LABELS[period]}` : base;
}

/** Дата из MySQL: строка 'YYYY-MM-DD HH:mm:ss' без часового пояса - разбираем вручную, чтобы не было сдвига */
export function fmtDbDateTime(s?: string | null): string {
  if (!s) return '-';
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
  if (!m) return s;
  return `${m[3]}.${m[2]}.${m[1].slice(2)} ${m[4]}:${m[5]}`;
}

export function fmtDbDate(s?: string | null): string {
  if (!s) return '-';
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return s;
  return `${m[3]}.${m[2]}.${m[1].slice(2)}`;
}

/** 'YYYY-MM-DD HH:mm:ss' → 'YYYY-MM-DD' для поля <input type="date"> */
export function toDateInput(s?: string | null): string {
  const m = s ? String(s).match(/^(\d{4}-\d{2}-\d{2})/) : null;
  return m ? m[1] : '';
}

/** Что изменилось между двумя снимками записи (для вкладки «История изменений») */
export function diffSnapshots(def: RuleDef | undefined, oldS: RuleSnapshot | null, newS: RuleSnapshot | null): string[] {
  const snap = oldS && newS ? [oldS, newS] : null;
  if (!snap) {
    const one = newS || oldS;
    if (!one) return [];
    const parts = [MODE_LABELS[one.mode]];
    if (one.mode !== 'off') parts.push(fmtRuleValue(def, one.value, one.period));
    if (one.valid_until) parts.push(`до ${fmtDbDate(one.valid_until)}`);
    return [parts.join(' · ')];
  }
  const [a, b] = snap;
  const out: string[] = [];
  if (a.mode !== b.mode) out.push(`Режим: ${MODE_LABELS[a.mode]} → ${MODE_LABELS[b.mode]}`);
  if (Number(a.value) !== Number(b.value) || a.period !== b.period) {
    out.push(`Значение: ${fmtRuleValue(def, a.value, a.period)} → ${fmtRuleValue(def, b.value, b.period)}`);
  }
  if ((a.valid_until || null) !== (b.valid_until || null)) {
    out.push(`Срок: ${a.valid_until ? fmtDbDate(a.valid_until) : 'бессрочно'} → ${b.valid_until ? fmtDbDate(b.valid_until) : 'бессрочно'}`);
  }
  if ((a.note || '') !== (b.note || '')) out.push('Изменено примечание');
  return out;
}
